<template>
    <div  v-if="isLargeScreen">
      <div>
        <div class="d-flex justify-content-between align-items-center">
          <Breadcrumbs main="Cards" title="Cards" />
          <!-- <button class="backOverview"><i class="bi bi-arrow-left me-2"></i>Back to cards overview</button> -->
        </div>
        
  
        <div class="container-fluid">
            <div v-if="hasCard" class="row widget-grid m-lg-5">
                <CardDetail />
            </div>
            <div v-else-if="step === 1" class="row widget-grid m-lg-5">
                <NoCard @apply="goApply" @activate="goActivate" />
            </div>
            <div v-else-if="step === 2" class="row widget-grid m-lg-5">
                <CardApplicationForm @back="step = 1" @success="handleFormSuccess" />
            </div>
            <div v-else-if="step === 3" class="row widget-grid m-lg-5">
                <CardApplicationSuccess @back="handleSuccessBack" />
            </div>
            <div v-else-if="step === 4" class="row widget-grid m-lg-5">
                <ActivateCard @back="step = 1" @success="handleActivateSuccess" :disableBack="disableBackOnActivate" />
            </div>
            <div v-else-if="step === 5" class="row widget-grid m-lg-5">
              <CardDetail />
              <HasCard @apply="step = 2" @activate="step = 4" @cardDetail="step = 5" />
            </div>
        </div>
      </div>
    </div>
    <div v-else>
            <div v-if="hasCard" class="row widget-grid m-lg-5">
                <MobileCardDetail />
            </div>
            <div v-else-if="step === 1" class="row widget-grid m-lg-5">
                <MobileNoCard @apply="goApply" @activate="goActivate" />
            </div>
            <div v-else-if="step === 2" class="row widget-grid m-lg-5">
                <MobileCardApplicationForm @back="step = 1" @success="handleFormSuccess" />
            </div>
            <div v-else-if="step === 3" class="row widget-grid m-lg-5">
                <MobileCardApplicationSuccess @back="handleSuccessBack" />
            </div>
            <div v-else-if="step === 4" class="row widget-grid m-lg-5">
                <MobileActivateCard @back="step = 1" @success="handleActivateSuccess" :disableBack="disableBackOnActivate" />
            </div>
            <div v-else-if="step === 5" class="row widget-grid m-lg-5">
              <MobileCardDetail />
              <MobileHasCard @apply="step = 2" @activate="step = 4" @cardDetail="step = 5" />
            </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onUnmounted, watch } from 'vue';
  import CardDetail from './cardDetail.vue';
  import HasCard from './hasCard.vue';
  import NoCard from './noCard.vue';
  import CardApplicationForm from './CardApplicationForm.vue';
  import CardApplicationSuccess from './CardApplicationSuccess.vue';
  import ActivateCard from './ActivateCard.vue';
  import MobileNoCard from './mobile/noCard.vue';
  import MobileHasCard from './mobile/hasCard.vue';
  import MobileCardApplicationForm from './mobile/CardApplicationForm.vue';
  import MobileCardApplicationSuccess from './mobile/CardApplicationSuccess.vue';
  import MobileActivateCard from './mobile/ActivateCard.vue';
  import MobileCardDetail from './mobile/cardDetail.vue';
  import { useCardStore } from '@/store/driver/card';
  import { fetchDriverInfo } from '@/store/public';

  const isLargeScreen = ref(true);
  const hasCard = ref(false);
  const step = ref(1);
  const disableBackOnActivate = ref(false); // 控制 ActivateCard 頁面的 Back 按鈕是否禁用

  const cardStore = useCardStore();

  const goApply = () => {
    step.value = 2;
  };

  const goActivate = () => {
    step.value = 4;
  };

  // 檢查是否已申請過卡片（可重複使用的函數）
  const checkAppliedCard = () => {
    const driver = JSON.parse(localStorage.getItem('Driver') || '{}');
    const accounts = driver?.accounts || [];
    return accounts.find(acc => acc.type === 4 && acc.status === 'application');
  };

  // 初始化卡片狀態
  const initializeCardState = async () => {
    try {
      await cardStore.fetchCardInfo();
      // 根據 cardStatus 判斷是否有卡片
      if (cardStore.cardStatus && cardStore.cardInfo && Object.keys(cardStore.cardInfo).length > 0) {
        hasCard.value = true;
        // 有卡片時，才從 localStorage 讀取保存的 step
        const savedStep = localStorage.getItem('cardStep');
        if (savedStep) step.value = Number(savedStep);
      } else {
        // 檢查是否有 type=4 且 enabled=1 的帳號（代表已申請過卡片）
        const appliedCardAccount = checkAppliedCard();
        
        if (appliedCardAccount) {
          // 如果已申請過卡片，直接導向 activate a new card 頁面
          hasCard.value = false;
          step.value = 4;
          disableBackOnActivate.value = true; // 禁用 Back 按鈕
        } else {
          hasCard.value = false;
          step.value = 1;
          disableBackOnActivate.value = false;
        }
      }
    } catch (error) {
      // 其他錯誤時，也導向 noCard
      hasCard.value = false;
      step.value = 1;
      disableBackOnActivate.value = false;
    }
  };

  onMounted(async () => {
    // 檢查螢幕大小
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    // 初始化卡片狀態
    await initializeCardState();
  });

  watch(step, (val) => {
    localStorage.setItem('cardStep', val);
    
    // 當進入 step 1 時，檢查是否已申請過卡片
    // 如果已申請過，自動跳轉到 step 4（啟用頁面）
    if (val === 1 && !hasCard.value) {
      const appliedCardAccount = checkAppliedCard();
      if (appliedCardAccount) {
        step.value = 4;
        disableBackOnActivate.value = true;
      }
    }
  });

  // 檢查螢幕大小
  function checkScreenSize() {
    isLargeScreen.value = window.innerWidth >= 992;
  }
  
  onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
  });

  async function handleFormSuccess() {
    // 申請成功後，重新獲取 Driver 資訊以更新 localStorage
    try {
      const driverToken = localStorage.getItem('driverToken');
      if (driverToken) {
        await fetchDriverInfo(driverToken);
      }
    } catch (error) {
      console.error('Failed to refresh driver info:', error);
    }
    
    // 申請成功後，先顯示成功頁面（Step 3）
    step.value = 3;
  }

  function handleSuccessBack() {
    // 在成功頁面點擊 Back 後，檢查是否已申請過卡片
    const appliedCardAccount = checkAppliedCard();
    if (appliedCardAccount) {
      // 如果已申請過卡片，導向啟用頁面
      step.value = 4;
      disableBackOnActivate.value = true;
    } else {
      // 如果沒有申請過，回到首頁
      step.value = 1;
    }
  }

    async function handleActivateSuccess() {
      try {
        // 啟用成功後，調用 getCardPlastic API 獲取卡片實體資訊
        await cardStore.getCardPlastic();
        // 設置 hasCard 為 true 並跳轉到卡片詳情頁面
        hasCard.value = true;
        step.value = 5;
      } catch (error) {
        console.error('Error after card activation:', error);
        // 即使 getCardPlastic 失敗，仍然跳轉到卡片詳情頁面
        hasCard.value = true;
        step.value = 5;
      }
    }
  </script>
  <style scoped>
    .backOverview {
      background: #00c962;
      color: black;
      border: none;
      border-radius: 24px;
      padding: 10px 32px;
      font-size: 1rem;
      font-weight: 500;
      cursor:pointer;
      margin-right: 160px;
    }
  </style>